import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useJobDetailsProvider } from '../../../../../../context/jobDetails';
import { useGlobalSearchProvider } from '../../../../../../context/globalSearch';
import DataTable from '../../../../../../../components/Common/UI/DataTable';
import deliveryInfoCol from './columns';
import {
  getSubTableColConfig,
  JobDetailColumnType,
  JobSubTableAccount,
  jobSubTableAccountsData,
  jobTypesPaper,
} from '../subTableConfig';
import { deliveryInfoData, DeliveryInfoRow } from './deliveryInfoData';
import Chart from './Chart';

function DeliveryInfo() {
  const { id = '' } = useParams<{ id: string }>();
  const { selectedTable } = useJobDetailsProvider();
  const { searchApplied } = useGlobalSearchProvider();

  const [deliveryInfoState, setDeliveryInfoState] = useState<DeliveryInfoRow[]>([]);
  const [jobSubTableData, setJobSubTableData] = useState<JobSubTableAccount[]>([]);

  /** Set data in main & sub tables on init */
  useEffect(() => {
    setDeliveryInfoState(deliveryInfoData);
  }, [deliveryInfoData]);
  useEffect(() => {
    setJobSubTableData(jobSubTableAccountsData);
  }, [jobSubTableAccountsData]);

  // DeliveryInfoColumnType <- Column types for delivery info.
  const handleFilterSubTable = (data: JobSubTableAccount[], types: JobDetailColumnType[]) => {
    const newData = data.filter(account => types.includes(account.jobType));

    setJobSubTableData(newData);
  };

  /** When user select table cell to which show more data, filter sub-table data depending on 'selectedTable' state */
  useEffect(() => {
    const { column, row } = selectedTable;

    if (row === 'INSERTS' && column === 'total') {
      setJobSubTableData(jobSubTableAccountsData.filter(item => item.accountName === 'inserts'));

      return;
    }

    if (column === 'percentage') {
      switch (row) {
        case 'PENDING': {
          setJobSubTableData(jobSubTableAccountsData.filter(item => item.status === 'Pending'));
          break;
        }
        case 'IN TRANSIT': {
          setJobSubTableData(jobSubTableAccountsData.filter(item => item.delivery === 'Transit'));
          break;
        }
        case '<3 DAYS': {
          setJobSubTableData(jobSubTableAccountsData.filter(item => item.days !== 'NA' && Number(item.days) < 3));
          break;
        }
        case '3-5 DAYS': {
          setJobSubTableData(
            jobSubTableAccountsData.filter(
              item => item.days !== 'NA' && Number(item.days) >= 3 && Number(item.days) <= 5,
            ),
          );
          break;
        }
        case '>5 DAYS': {
          setJobSubTableData(jobSubTableAccountsData.filter(item => item.days !== 'NA' && Number(item.days) > 5));
          break;
        }
        case 'EXCEPTION MAIL': {
          setJobSubTableData(jobSubTableAccountsData.filter(item => item.delivery === 'Exception Mail'));
          break;
        }
        case 'TOTAL VOLUME': {
          handleFilterSubTable(jobSubTableAccountsData, jobTypesPaper);
          break;
        }
        default:
          break;
      }

      return;
    }

    if (!column || column === 'total') {
      // If row is 'ACCOUNTS'
      // This page works only with paper columns at the moment| id === 'Electronic' ? jobTypesElectronic : jobTypesPaper
      handleFilterSubTable(jobSubTableAccountsData, jobTypesPaper);
    } else {
      handleFilterSubTable(jobSubTableAccountsData, [column as JobDetailColumnType]);
    }
  }, [selectedTable.column, selectedTable.row]);

  /** Filter current data thru global search before show */
  const filteredThruSearchData = useMemo(() => {
    const { search: appliedSearch, field } = searchApplied;

    /** If both search and field-key exist, then do simple comparison by single key. */
    if (appliedSearch && field) {
      return jobSubTableData.filter(item =>
        String(item[field]).toLowerCase().includes(String(appliedSearch).toLowerCase()),
      );
    }

    /** If key not specified then search in each object value */
    if (appliedSearch) {
      const searchString = String(appliedSearch).toLowerCase();

      return jobSubTableData.filter(item =>
        Object.values(item).some(
          value =>
            // Searching for only string & number values, avoiding null & undefined
            (typeof value === 'string' && value.toLowerCase().includes(searchString)) ||
            (typeof value === 'number' && String(value).includes(searchString)),
        ),
      );
    }

    return jobSubTableData;
  }, [jobSubTableData, searchApplied]);

  return (
    <div className="flex flex-col gap-4">
      <div className="mx-4 mt-4 grid grid-cols-2 gap-4">
        <DataTable
          columns={deliveryInfoCol}
          data={deliveryInfoState}
          variant="bordered"
          paginationPosition="none"
          className="rounded"
        />

        <div className="bg-slate-100 rounded-lg border flex items-center justify-center">
          <Chart />
        </div>
      </div>

      <div className="h-[24px]" />
      {selectedTable.column ? (
        <div>
          <DataTable
            columns={getSubTableColConfig(id, selectedTable.row, true)}
            data={filteredThruSearchData}
            variant="bordered"
            paginationPosition="bottom"
          />
        </div>
      ) : null}
    </div>
  );
}

export default DeliveryInfo;
